import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  X, FileText, ChevronDown, ChevronRight, ArrowRight, Plus, Trash2, MessageSquare, Send,
  Paperclip, Download, Columns,
  // 완료 승인 (2026-09-04) — 승인 ✓ · 반려 ↺ · 대기 ⏱
  Check, RotateCcw, Clock,
  /*
   * ⭐ `ClipboardList` 를 ★ 더했다 — ⇒ 정본은 ★ 서식 아이콘의 폴백으로 ⛔ 이모지(📋)를 썼다.
   * ⇒ CLAUDE.md 디자인 가이드: 「… 인라인 hex 금지 · ★ 이모지 금지」
   * ⇒ ⭐⭐ 그리고 ★ 이 결정은 ⇒ ⭐ «내가 2-2′ 에서 이미 한 것»이다 — ⇒ 같은 폴백, 같은 부품:
   *     pages/ProjectV2/ChecklistTemplatesV2Page.tsx:201  {t.icon ?? <ClipboardList size={14} />}
   *   ⇒ ⇒ ★ 그래서 ⭐ 새로 고르지 않고 ★ 그 꼴을 ⭐ 그대로 쓴다(⇒ ⛔ 「같은 뜻을 두 글자로」를 피한다).
   * ⇒ ⚠ 사용자가 저장한 `t.icon` 은 ★ 그대로 그린다 — ⇒ ⭐ 그것은 «내 글자»가 아니라 «데이터»다.
   */
  ClipboardList,
} from '../../icons';
import {
  getTask, patchTask, deleteTask, listProjects,
  listTaskComments, createTaskComment, deleteTaskComment,
  listTaskAttachments, addTaskAttachment, deleteTaskAttachment,
  // 완료 승인 (2026-09-04)
  submitTaskForApproval, approveTask, rejectTask,
  type TaskDto, type TaskCommentDto, type TaskAttachmentDto,
  TASK_PRIORITY_META, type TaskPriority,
} from '../../api/tasks';
import { uploadChatAttachment, requestPresignedDownload } from '../../api/files';
import { listTeamMembers } from '../../api/project-teams';
// 태스크 상세(TaskDetailModal)와 **같은 부품**을 쓴다 — 두 화면의 표시·조작이 갈라지지 않게.
import { AttachmentTypeIcon } from '../TaskPage/AttachmentTypeIcon';
import { FieldCell } from './custom-field-ui';
import {
  listCustomFields, setCustomFieldValue,
  type CustomFieldDto, type CustomFieldValue,
} from '../../api/task-extras';
import { Avatar } from '../../components/shared';
import { useAuthStore } from '../../store/authStore';
import {
  listChecklist, addChecklistItem, patchChecklistItem, deleteChecklistItem, importChecklistTemplate,
  listChecklistTemplates, setTaskGroup,
  type ChecklistItemDto, type ChecklistTemplateDto, type TaskGroupDto,
} from '../../api/task-extras';
import { layerAlert, layerConfirm } from '../../components/LayerPopup/LayerPopup';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import { dueState, dueStateLabel } from '../ProjectPage/my-task-due';
import './MyTasksBoard.css';
// 완료 승인 (2026-09-04) — 이 기능이 그리는 것들의 스타일. 기존 CSS 는 건드리지 않는다.
import './TaskApproval.css';

interface MyTaskDetailPanelProps {
  taskId: string;
  /** 목록 행이 이미 알고 있는 값 — 상세 응답이 오기 전 헤더를 비우지 않기 위한 초기 표시. */
  seed: { title: string; status: string; priority: string; dueDate: string | null; projectName: string; groupName?: string | null };
  groups: TaskGroupDto[];
  currentGroupId: string | null;
  onClose: () => void;
  /** 상태·그룹이 바뀌면 목록을 갱신한다. */
  onChanged: () => void;
  /** [프로젝트에서 보기 →] — 이 업무가 속한 프로젝트 보드로. */
  onGoToProject: () => void;
  /**
   * 2026-09-01 — 업무를 지웠을 때. 부모가 패널을 닫고 목록을 다시 읽는 경로다.
   *
   * 종전에 이 패널에는 **업무 삭제가 없었다** — 첨부·코멘트만 지울 수 있었고, 정작 업무
   * 자체는 [프로젝트에서 보기 →] 로 칸반까지 건너가 태스크 상세(TaskDetailModal)를 열어야
   * 지울 수 있었다. 같은 업무가 한 화면에서는 지워지고 다른 화면에서는 안 지워졌다는 뜻이다.
   *
   * 스페이스·프로젝트 카드의 삭제와 같은 계약 — **주지 않으면 삭제 버튼도 뜨지 않는다.**
   * (지운 뒤 패널이 그대로 열려 있으면 이미 없는 업무를 보고 있게 되기 때문.)
   */
  onDeleted?: () => void;
}

/**
 * MyTaskDetailPanel (AW-004-03, 2026-08-18) — 내 업무 **상세 슬라이드오버**.
 *
 * 설계서의 지적은 "내 할일에서 선택해도 프로젝트로만 이동하지 무슨 내용인지 연결이 안 된다" 였다.
 * 그래서 목록에서 제목이나 '›' 를 누르면 **그 자리에서** 오른쪽 패널이 열리고, 프로젝트로
 * 이동하는 것은 [프로젝트에서 보기 →] 라는 명시적인 한 걸음으로 남긴다.
 *
 * 체크리스트는 두 길로 채운다:
 *   ① 하단 입력줄로 항목 하나씩,
 *   ② [템플릿에서 가져오기] — 템플릿을 펼쳐 기본 항목을 미리 보고 한 번에 추가(AW-004-04 연동).
 * 가져오기는 복사라서, 나중에 템플릿을 고쳐도 이 업무의 체크리스트는 흔들리지 않는다.
 */
export function MyTaskDetailPanel({
  taskId, seed, groups, currentGroupId, onClose, onChanged, onGoToProject, onDeleted,
}: MyTaskDetailPanelProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const [task, setTask] = useState<TaskDto | null>(null);
  const [items, setItems] = useState<ChecklistItemDto[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplateDto[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openTemplate, setOpenTemplate] = useState<string | null>(null);
  // 가져오기가 막힌 이유(빈 템플릿 등) — 픽커 안에 그대로 띄운다.
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const draftRef = useRef<HTMLInputElement | null>(null);

  /**
   * 코멘트 (2026-08-21) — 태스크 상세(TaskDetailModal)에만 있던 것을 여기에도 둔다.
   *
   * "내 할일에서 선택해도 무슨 내용인지 연결이 안 된다" 가 이 패널을 만든 이유였는데,
   * 정작 그 태스크에 오간 이야기는 프로젝트로 건너가야 볼 수 있었다. 같은 API 를 쓴다.
   * 자리는 **체크리스트 위** — 설명과 코멘트로 맥락을 먼저 읽고 할 일을 보는 순서다.
   */
  const me = useAuthStore((st) => st.user);
  const [comments, setComments] = useState<TaskCommentDto[]>([]);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);

  /**
   * 첨부 파일 · 추가 정보 (2026-08-21) — 태스크 상세에만 있던 나머지를 여기로 잇는다.
   *
   * "내 할일에서 업무 제목을 누르면 태스크 상세 내용이 연동되도록" — 그 전까지 이 패널은
   * 제목·기한·그룹·체크리스트만 보여 줘서, 같은 태스크인데 두 화면이 서로 다른 것을 담고 있었다.
   * 서버는 태스크 상세와 **같은 API** 를 쓴다(신규 엔드포인트 0).
   */
  const [attachments, setAttachments] = useState<TaskAttachmentDto[]>([]);
  const [attachBusy, setAttachBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [cfFields, setCfFields] = useState<CustomFieldDto[]>([]);
  const [cfValues, setCfValues] = useState<Record<string, CustomFieldValue>>({});
  const [cfMembers, setCfMembers] = useState<Array<{ userId: string; name: string }>>([]);

  useEscapeClose(onClose, busy);

  useEffect(() => {
    let alive = true;
    void getTask(taskId).then((t) => { if (alive) setTask(t); }).catch(() => { /* seed 로 표시 유지 */ });
    void listChecklist(taskId).then((rows) => { if (alive) setItems(rows); }).catch(() => { /* 빈 목록 */ });
    void listTaskComments(taskId).then((rows) => { if (alive) setComments(rows); }).catch(() => { /* 빈 목록 */ });
    void listTaskAttachments(taskId).then((rows) => { if (alive) setAttachments(rows ?? []); }).catch(() => { /* 빈 목록 */ });
    return () => { alive = false; };
  }, [taskId]);

  /**
   * 추가 정보(커스텀 필드) — 정의·값은 **프로젝트** 단위라 태스크 응답이 온 뒤에야 읽을 수 있다.
   *
   * MEMBER 타입 필드의 선택지로 팀원이 필요한데, 이 패널은 teamId 를 받지 않는다(목록이
   * 넘겨 주는 것은 태스크뿐). 프로젝트 목록에서 이 프로젝트의 teamId 를 찾아 쓴다 —
   * 호출부(MyTasksBoard)에 prop 을 새로 요구하지 않으려는 선택이다.
   */
  useEffect(() => {
    const projectId = task?.projectId;
    if (!projectId) return;
    let alive = true;
    void listCustomFields(projectId)
      .then((res) => {
        if (!alive) return;
        setCfFields(res.fields);
        const map: Record<string, CustomFieldValue> = {};
        for (const v of res.values) if (v.taskId === taskId) map[v.fieldId] = v.value;
        setCfValues(map);
      })
      .catch(() => { if (alive) setCfFields([]); });
    void listProjects()
      .then(async (ps) => {
        const teamId = ps.find((p) => p.id === projectId)?.teamId ?? null;
        if (!teamId || !alive) return;
        const ms = await listTeamMembers(teamId);
        if (alive) setCfMembers((ms ?? []).map((m) => ({ userId: m.userId, name: m.name })));
      })
      .catch(() => { /* 후보 없이도 나머지 타입은 그대로 쓸 수 있다 */ });
    return () => { alive = false; };
  }, [task?.projectId, taskId]);

  const done = items.filter((i) => i.isDone).length;
  /** 아직 체크되지 않은 항목 수 — 완료를 막는 근거. */
  const remaining = items.length - done;
  const status = task?.status ?? seed.status;
  const priority = (task?.priority ?? seed.priority) as TaskPriority;
  const dueDate = task?.dueDate ?? seed.dueDate;
  const pm = TASK_PRIORITY_META[priority] ?? null;
  const ds = useMemo(() => dueState(dueDate, status), [dueDate, status]);
  /**
   * 완료가 막힌 상태 — 체크리스트가 하나라도 남아 있으면 참(2026-08-21).
   * 체크리스트가 아예 없는 업무(items.length === 0)는 remaining 이 0 이라 종전대로 바로 완료된다.
   * 되돌리기(완료 → 진행 중)는 막지 않는다 — 되돌리는 쪽은 잃는 것이 없다.
   */
  const blocked = status !== 'DONE' && remaining > 0;

  /* ── 완료 승인 (2026-09-04) ──────────────────────────────────────────────
   * 승인을 켠 프로젝트에서는 [완료 처리] 가 [완료 요청] 으로 바뀌고, 승인권자에게만
   * [승인]·[반려] 가 선다. 판정값(requiresDoneApproval · canApproveDone)은 전부 서버가
   * 준 것을 그대로 쓴다 — 화면이 역할을 추측하면 서버와 어긋나 403 나는 버튼이 생긴다.
   * 상세 응답이 오기 전(task === null)에는 종전 버튼을 그대로 둔다(값을 모르므로).
   */
  const apOn = task?.requiresDoneApproval === true;
  const apPending = apOn && status === 'REVIEW';
  const canApprove = apOn && task?.canApproveDone === true;
  const rejectedReason = apOn && status !== 'DONE' && status !== 'REVIEW' ? (task?.rejectReason ?? null) : null;
  const [apBusy, setApBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [apError, setApError] = useState<string | null>(null);

  /** 승인 라우트 셋의 공통 뒷정리 — 상태를 화면에 반영하고 목록을 다시 읽는다. */
  async function runApproval(fn: () => Promise<{ status: string; approvedBy: string | null; approvedAt: string | null; rejectReason: string | null }>) {
    setApBusy(true);
    setApError(null);
    try {
      const r = await fn();
      // 상세는 부분 갱신한다 — 전체를 다시 받으면 열려 있는 코멘트·체크리스트가 깜빡인다.
      setTask((cur) => (cur ? { ...cur, ...r, status: r.status } as TaskDto : cur));
      setRejectOpen(false);
      setRejectReason('');
      onChanged();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setApError(msg ?? tProject('project.mytask.ap_err'));
    } finally {
      setApBusy(false);
    }
  }

  async function openPicker() {
    setPickerOpen((v) => !v);
    if (templates.length === 0) {
      try {
        setTemplates(await listChecklistTemplates());
      } catch {
        setTemplates([]);
      }
    }
  }

  async function addItem() {
    const c = draft.trim();
    if (!c || busy) return;
    setBusy(true);
    try {
      const created = await addChecklistItem(taskId, c);
      setItems((cur) => [...cur, created]);
      setDraft('');
      draftRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  async function toggleItem(item: ChecklistItemDto) {
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, isDone: !i.isDone } : i))); // optimistic
    try {
      await patchChecklistItem(taskId, item.id, { isDone: !item.isDone });
    } catch {
      setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, isDone: item.isDone } : i)));
    }
  }

  async function removeItem(item: ChecklistItemDto) {
    const prev = items;
    setItems((cur) => cur.filter((i) => i.id !== item.id));
    try {
      await deleteChecklistItem(taskId, item.id);
    } catch {
      setItems(prev);
    }
  }

  async function importTemplate(t: ChecklistTemplateDto) {
    // 기본 항목이 없는 템플릿은 가져올 게 없다 — 버튼을 죽여 놓는 대신 이유를 말한다.
    if (t.items.length === 0) {
      setPickerError(tProject('project.mytask.import_empty', { name: t.name }));
      setOpenTemplate(t.id);
      return;
    }
    setPickerError(null);
    setBusy(true);
    try {
      const res = await importChecklistTemplate(taskId, t.id);
      setItems(res.items);
      setPickerOpen(false);
      setOpenTemplate(null);
    } catch (err) {
      // 상한 초과 등으로 실패할 수 있다 — 조용히 넘기면 "눌러도 아무 일이 없다" 가 된다.
      setPickerError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? tProject('project.mytask.err_import'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * 완료 처리 / 되돌리기.
   *
   * 종전에는 이미 완료된 업무에서 버튼을 `disabled` 로 죽여 놨다. 체크리스트를 가져온 뒤
   * 회색 버튼만 남으니 "가져오기 때문에 막혔다" 로 읽혔다(2026-08-19 신고) — 실제로는
   * 그 업무가 이미 완료 상태였을 뿐이고, 되돌릴 길도 화면에 없었다.
   * 이제 상태에 따라 [완료 처리] ↔ [완료 취소] 로 바뀐다.
   */
  async function toggleDone() {
    const next = status === 'DONE' ? 'TODO' : 'DONE';
    setBusy(true);
    try {
      const updated = await patchTask(taskId, { status: next });
      setTask(updated);
      onChanged();
      // 완료로 넘길 때만 닫는다(기존 동작). 되돌리기는 계속 보면서 이어 작업하는 흐름이라 열어 둔다.
      if (next === 'DONE') onClose();
    } catch (err) {
      setPickerError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * 업무 삭제 (2026-09-01) — 태스크 상세(TaskDetailModal)의 [삭제]와 **같은 동작**이다.
   * 같은 API(`deleteTask`, soft delete), 같은 확인 문구, 같은 자리(하단 왼쪽 끝).
   *
   * 목록의 행에는 달지 않았다 — 행 오른쪽 자리는 이미 우선순위·미루기·열기 셋이 쓰고 있고,
   * 수십 줄이 촘촘한 표에서 [미루기] 옆에 되돌릴 수 없는 동작을 두면 오클릭이 난다.
   * 목록에서 업무를 치우는 정상 경로는 완료 토글(되돌리기까지 있다)이다.
   *
   * 노출은 **작성자 본인**에게만 — 태스크 상세의 canEdit 와 같은 규칙이다. 서버는 이보다
   * 넓게 열려 있지만(MANAGER+ 또는 작성자), 두 화면이 서로 다른 조건으로 버튼을 띄우면
   * "칸반에선 없던 버튼이 여기선 있다" 가 된다. 넓은 쪽은 서버가 계속 받아 준다.
   */
  const canDelete = onDeleted != null && task != null && me != null && String(me.id) === task.createdBy;

  async function handleDelete() {
    const title = task?.title ?? seed.title;
    const ok = await layerConfirm(
      `${tProject('project.mytask.delete_confirm', { title })}
${tProject('project.mytask.delete_irreversible')}`,
      { danger: true },
    );
    if (!ok) return;
    setBusy(true);
    try {
      await deleteTask(taskId);
      // 닫기가 먼저다 — 목록을 다시 읽는 사이 이미 없는 업무의 패널이 남아 있지 않게.
      onDeleted?.();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      void layerAlert(code === 'FORBIDDEN'
        ? tProject('project.mytask.delete_err_forbidden')
        : tProject('project.mytask.delete_err'));
      setBusy(false);
    }
  }

  async function attachFiles(files: FileList | null) {
    if (!files || files.length === 0 || attachBusy) return;
    setAttachBusy(true);
    try {
      // 태스크 상세와 같은 상한(20개) — 두 화면이 다른 한도를 쓰면 한쪽에서만 막힌다.
      for (const f of Array.from(files).slice(0, 20 - attachments.length)) {
        const up = await uploadChatAttachment(f);
        await addTaskAttachment(taskId, up.fileId);
      }
      setAttachments(await listTaskAttachments(taskId));
    } catch (err) {
      setPickerError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? tProject('project.mytask.attach_err'));
    } finally {
      setAttachBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function downloadAttachment(a: TaskAttachmentDto) {
    try {
      const { downloadUrl } = await requestPresignedDownload(a.fileId);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch {
      setPickerError(tProject('project.mytask.attach_err'));
    }
  }

  async function removeAttachment(a: TaskAttachmentDto) {
    const prev = attachments;
    setAttachments((cur) => cur.filter((x) => x.id !== a.id)); // optimistic
    try {
      await deleteTaskAttachment(taskId, a.id);
    } catch {
      setAttachments(prev);
      setPickerError(tProject('project.mytask.attach_err'));
    }
  }

  /** 추가 정보 저장 — 표·태스크 상세와 같이 고치는 즉시 서버로 보낸다. */
  async function saveCustomField(field: CustomFieldDto, next: CustomFieldValue) {
    const prev = cfValues[field.id] ?? null;
    setCfValues((v) => ({ ...v, [field.id]: next }));
    try {
      await setCustomFieldValue(taskId, field.id, next);
    } catch {
      setCfValues((v) => ({ ...v, [field.id]: prev }));
      setPickerError(tProject('project.mytask.extra_err'));
    }
  }

  async function addComment() {
    const c = commentDraft.trim();
    if (!c || commentBusy) return;
    setCommentBusy(true);
    try {
      const created = await createTaskComment(taskId, c);
      setComments((cur) => [...cur, created]);
      setCommentDraft('');
    } catch (err) {
      setPickerError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? tProject('project.mytask.comment_err'));
    } finally {
      setCommentBusy(false);
    }
  }

  async function removeComment(c: TaskCommentDto) {
    const prev = comments;
    setComments((cur) => cur.filter((x) => x.id !== c.id)); // optimistic
    try {
      await deleteTaskComment(taskId, c.id);
    } catch {
      setComments(prev);
    }
  }

  async function changeGroup(groupId: string) {
    try {
      await setTaskGroup(taskId, groupId || null);
      onChanged();
    } catch {
      /* 실패 시 목록은 그대로 — 다음 조회에서 서버 값이 이긴다. */
    }
  }

  return (
    <>
      <div className="mtp-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="mtp-panel" role="dialog" aria-modal="true" aria-label={seed.title}>
        {/* 닫기는 2026-08-21 에 하단 [완료 처리] 옆으로 내렸다 — 머리에는 위치만 남긴다. */}
        <header className="mtp-head">
          <span className="mtp-crumb">
            <i className="mtp-crumb-dot" aria-hidden />
            {seed.projectName}
            {seed.groupName ? ` · ${seed.groupName}` : ''}
          </span>
        </header>

        <div className="mtp-body scrollbar-thin">
          <div className="mtp-title-row">
            <span className={`mtp-check${status === 'DONE' ? ' mtp-check--on' : ''}`} aria-hidden />
            <h2 className="mtp-title">{task?.title ?? seed.title}</h2>
          </div>

          <div className="mtp-meta">
            {pm && <span className="mtp-pri">{pm.label}</span>}
            {ds && <span className={`mtp-due mtp-due--${ds}`}>{dueStateLabel(ds, dueDate)}</span>}
            {dueDate && <span className="mtp-duedate">{tProject('project.mytask.due_on', { date: dueDate.replace(/-/g, '.') })}</span>}
            {/*
              완료 승인 (2026-09-04) — 지금 어디에 서 있는지를 제목 바로 아래에서 알린다.
              대기 중이면 '승인 대기', 반려로 돌아왔으면 '반려됨' + 사유. 둘 다 승인을 켠
              프로젝트에서만 값이 차므로, 끈 프로젝트에서는 이 줄이 종전과 똑같다.
            */}
            {apPending && (
              <span className="mtb-ap mtb-ap--pending">
                <Clock size={11} aria-hidden />
                {tProject('project.mytask.ap_pending')}
              </span>
            )}
            {rejectedReason !== null && (
              <span className="mtb-ap mtb-ap--rejected">
                <RotateCcw size={11} aria-hidden />
                {tProject('project.mytask.ap_rejected')}
              </span>
            )}
          </div>
          {rejectedReason && (
            <p className="mtp-desc" style={{ color: '#b91c1c' }}>
              {tProject('project.mytask.ap_rejected_reason', { reason: rejectedReason })}
            </p>
          )}

          {/* 설명 — 테두리도 제목도 없는 맨 문단이라 무슨 칸인지 안 보였다(2026-09-07 지시).
              형제 구획(첨부·코멘트·체크리스트)과 같은 mtp-sec 머리를 얹고 테두리를 준다.
              기존 <p className="mtp-desc"> 는 그대로 두고 감싸기만 한다. */}
          {task?.description && (
            <div className="mtp-sec mtp-sec--desc">
              <div className="mtp-sec-head">
                <FileText size={13} aria-hidden />
                {tProject('project.mytask.description')}
              </div>
              <p className="mtp-desc">{task.description}</p>
            </div>
          )}

          {groups.length > 0 && (
            <div className="mtp-group">
              <label className="mtp-group-lb" htmlFor="mtp-group">{tProject('project.mytask.group_label')}</label>
              <select
                id="mtp-group"
                className="mtp-group-select"
                value={currentGroupId ?? ''}
                onChange={(e) => void changeGroup(e.target.value)}
              >
                <option value="">{tProject('project.mytask.group_none')}</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          )}

          {/* 첨부 파일 — 태스크 상세와 같은 순서(첨부 → 추가 정보 → 코멘트)로 세운다. */}
          <div className="mtp-sec">
            <div className="mtp-sec-head">
              <Paperclip size={13} aria-hidden />
              {tProject('project.mytask.attachments', { n: String(attachments.length) })}
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => void attachFiles(e.target.files)}
            />
            {attachments.length === 0 ? (
              <p className="mtp-sec-empty">{tProject('project.mytask.attach_empty')}</p>
            ) : (
              <ul className="mtp-att-list">
                {attachments.map((a) => (
                  <li key={a.id} className="mtp-att-item">
                    <AttachmentTypeIcon mimeType={a.mimeType} name={a.name} />
                    <span className="mtp-att-name" title={a.name}>{a.name}</span>
                    <span className="mtp-att-size">{Math.max(1, Math.round(a.sizeBytes / 1024))}KB</span>
                    <button
                      type="button"
                      className="mtp-att-act"
                      onClick={() => void downloadAttachment(a)}
                      aria-label={tProject('project.mytask.attach_download', { name: a.name })}
                      data-tip={tProject('project.mytask.attach_download', { name: a.name })}
                    >
                      <Download size={12} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="mtp-att-act mtp-att-act--del"
                      onClick={() => void removeAttachment(a)}
                      disabled={attachBusy}
                      aria-label={tProject('project.mytask.attach_delete', { name: a.name })}
                      data-tip={tProject('project.mytask.attach_delete', { name: a.name })}
                    >
                      <Trash2 size={12} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="mtp-att-add"
              onClick={() => fileRef.current?.click()}
              disabled={attachBusy || attachments.length >= 20}
            >
              <Paperclip size={12} aria-hidden />
              {tProject('project.mytask.attach_add')}
            </button>
          </div>

          {/* 추가 정보 — 목록보기 표의 ＋ 로 만든 커스텀 필드. 태스크 상세와 같은 편집기(FieldCell). */}
          {cfFields.length > 0 && (
            <div className="mtp-sec">
              <div className="mtp-sec-head">
                <Columns size={13} aria-hidden />
                {tProject('project.mytask.extra', { n: String(cfFields.length) })}
              </div>
              <ul className="mtp-extra-list">
                {cfFields.map((f) => (
                  <li key={f.id} className="mtp-extra-row">
                    <span className="mtp-extra-label" title={f.name}>{f.name}</span>
                    <span className="mtp-extra-value">
                      <FieldCell
                        field={f}
                        value={cfValues[f.id] ?? null}
                        members={cfMembers}
                        disabled={busy}
                        onChange={(next) => void saveCustomField(f, next)}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 코멘트 — 설명과 함께 **체크리스트 위**에 둔다(2026-08-21 지시). */}
          <div className="mtp-cm">
            <div className="mtp-cm-head">
              <MessageSquare size={13} aria-hidden />
              {tProject('project.mytask.comments', { n: String(comments.length) })}
            </div>
            {comments.length === 0 ? (
              <p className="mtp-cm-empty">{tProject('project.mytask.comment_empty')}</p>
            ) : (
              <ul className="mtp-cm-list">
                {comments.map((c) => (
                  <li key={c.id} className="mtp-cm-item">
                    <Avatar userId={Number(c.authorId) || 0} name={c.authorName} src={c.authorAvatarUrl} size="xs" />
                    <div className="mtp-cm-body">
                      <div className="mtp-cm-meta">
                        <b>{c.authorName}</b>
                        <time dateTime={c.createdAt}>{c.createdAt.slice(0, 10).replace(/-/g, '.')}</time>
                      </div>
                      <p className="mtp-cm-text">{c.content}</p>
                    </div>
                    {/* 남의 코멘트는 지울 수 없다 — 태스크 상세와 같은 규칙. */}
                    {me !== null && String(me.id) === c.authorId && (
                      <button
                        type="button"
                        className="mtp-cm-del"
                        aria-label={tProject('project.mytask.comment_delete')}
                        data-tip={tProject('project.mytask.comment_delete')}
                        onClick={() => void removeComment(c)}
                      >
                        <Trash2 size={12} aria-hidden />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="mtp-cm-add">
              <input
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                placeholder={tProject('project.mytask.comment_ph')}
                aria-label={tProject('project.mytask.comment_ph')}
                onKeyDown={(e) => { if (e.key === 'Enter') void addComment(); }}
                disabled={commentBusy}
              />
              <button
                type="button"
                className="mtp-cm-send"
                onClick={() => void addComment()}
                disabled={commentBusy || commentDraft.trim() === ''}
                aria-label={tProject('project.mytask.comment_send')}
                data-tip={tProject('project.mytask.comment_send')}
              >
                <Send size={13} aria-hidden />
              </button>
            </div>
          </div>

          <div className="mtp-cl-head">
            <span className="mtp-cl-title">
              {tProject('project.mytask.checklist', { done: String(done), total: String(items.length) })}
            </span>
            {/* 이미 완료로 넘어간 옛 업무 중 남은 항목이 있는 것 — 숨기지 않고 사실대로 알린다. */}
            {status === 'DONE' && remaining > 0 && (
              <span className="mtp-cl-warn" role="status">
                {tProject('project.mytask.done_but_left', { n: String(remaining) })}
              </span>
            )}
            <button type="button" className="mtp-tmpl-btn" onClick={() => void openPicker()} aria-expanded={pickerOpen}>
              <FileText size={12} aria-hidden />
              {tProject('project.mytask.from_template')}
            </button>
          </div>

          {pickerOpen && (
            <div className="mtp-tmpl-box">
              <p className="mtp-tmpl-hint">{tProject('project.mytask.template_hint')}</p>
              {pickerError && <p className="mtp-tmpl-err" role="alert">{pickerError}</p>}
              {templates.length === 0 && <p className="mtp-tmpl-empty">{tProject('project.mytask.template_empty')}</p>}
              {templates.map((t) => {
                const open = openTemplate === t.id;
                return (
                  <div className={`mtp-tmpl${open ? ' mtp-tmpl--on' : ''}`} key={t.id}>
                    <button
                      type="button"
                      className="mtp-tmpl-row"
                      onClick={() => setOpenTemplate(open ? null : t.id)}
                      aria-expanded={open}
                    >
                      {open ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
                      <span className="mtp-tmpl-ico" aria-hidden>{t.icon ?? <ClipboardList size={14} />}</span>
                      <span className="mtp-tmpl-body">
                        <b>{t.name}</b>
                        <small>
                          {tProject(t.scope === 'SHARED' ? 'project.tmpl.shared' : 'project.tmpl.private')}
                          {' · '}
                          {tProject('project.tmpl.items_n', { n: String(t.items.length) })}
                        </small>
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`mtp-tmpl-import${t.items.length === 0 ? ' mtp-tmpl-import--empty' : ''}`}
                      disabled={busy}
                      onClick={() => void importTemplate(t)}
                    >
                      {tProject('project.mytask.import')}
                    </button>
                    {open && (
                      <ul className="mtp-tmpl-items">
                        {t.items.map((c, i) => (
                          <li key={`${t.id}-${i}`}>
                            <span className="mtp-tmpl-box-icon" aria-hidden />
                            {c}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <ul className="mtp-cl">
            {items.map((it) => (
              <li key={it.id} className={it.isDone ? 'mtp-cl-item mtp-cl-item--done' : 'mtp-cl-item'}>
                <input
                  type="checkbox"
                  checked={it.isDone}
                  onChange={() => void toggleItem(it)}
                  aria-label={it.content}
                />
                <span className="mtp-cl-text">{it.content}</span>
                <button
                  type="button"
                  className="mtp-cl-del"
                  aria-label={tProject('project.mytask.item_delete_aria', { name: it.content })}
                  onClick={() => void removeItem(it)}
                >
                  <Trash2 size={12} aria-hidden />
                </button>
              </li>
            ))}
          </ul>

          <div className="mtp-cl-add">
            <Plus size={13} aria-hidden />
            <input
              ref={draftRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={tProject('project.mytask.item_ph')}
              aria-label={tProject('project.mytask.item_ph')}
              onKeyDown={(e) => { if (e.key === 'Enter') void addItem(); }}
              disabled={busy}
            />
          </div>
        </div>

        <footer className="mtp-foot">
          {/*
            막힌 이유는 버튼 줄 **위**에 따로 놓는다(2026-08-21).
            버튼 옆에 두니 460px 폭에서 [프로젝트에서 보기] 가 다음 줄로 밀려 버튼이 두 줄이
            됐다. 문장을 한 줄 위로 올리면 버튼 셋(완료 처리 · 닫기 · 프로젝트에서 보기)이
            한 줄에 들어가고, 이유는 바로 위에 그대로 보인다.
          */}
          {blocked && (
            <p className="mtp-left-hint" role="status">
              {tProject('project.mytask.blocked_hint', { n: String(remaining) })}
            </p>
          )}
          <div className="mtp-foot-btns">
          {/*
            삭제는 줄 맨 왼쪽에 따로 세운다(margin-right:auto) — 태스크 상세 모달의 하단과
            같은 배치다. 되돌릴 수 없는 동작이라 [완료 처리]·[닫기] 와 붙여 두지 않는다.
          */}
          {canDelete && (
            <button
              type="button"
              className="mtp-del"
              onClick={() => void handleDelete()}
              disabled={busy}
              aria-label={tProject('project.mytask.delete_aria', { title: task?.title ?? seed.title })}
              data-tip={tProject('project.mytask.delete_aria', { title: task?.title ?? seed.title })}
            >
              <Trash2 size={13} aria-hidden />
              {tProject('project.mytask.delete')}
            </button>
          )}
          {/*
            완료는 체크리스트가 전부 체크된 뒤에만 눌린다(2026-08-21).
            종전에는 남은 항목이 있으면 확인 줄을 펴고 [남은 항목 체크하고 완료] 로 한 번에
            넘어갈 수 있었다 — 체크하지 않은 일이 체크된 것으로 바뀌어 버려, 체크리스트를
            만들어 둔 의미가 다시 없어졌다. 이제는 버튼 자체가 죽고, 왜 죽었는지를 옆에 적는다.
            하지 않기로 한 항목은 목록의 휴지통으로 지우면 그만큼 remaining 이 줄어 풀린다.
          */}
          {/*
            완료 승인 (2026-09-04) — 승인을 켠 프로젝트에서만 이 갈래가 산다.
            끈 프로젝트(apOn=false)에서는 아래 else 로 떨어져 종전 버튼 그대로다.

              대기 아님 + 승인 ON   → [완료 요청]   (담당자)
              대기 중  + 승인권 O   → [승인] [반려] (상사)
              대기 중  + 승인권 X   → '승인 대기' 알림만 (담당자 — 되돌릴 것이 없다)
              완료됨               → [진행 중으로 되돌리기] (종전 그대로)
          */}
          {apOn && status !== 'DONE' ? (
            apPending ? (
              canApprove ? (
                <>
                  <button
                    type="button"
                    className="mtb-apbtn mtb-apbtn--approve"
                    onClick={() => void runApproval(() => approveTask(taskId))}
                    disabled={apBusy}
                    aria-label={tProject('project.mytask.ap_approve_aria', { name: task?.title ?? seed.title })}
                  >
                    <Check size={13} aria-hidden />
                    {tProject('project.mytask.ap_approve')}
                  </button>
                  <button
                    type="button"
                    className="mtb-apbtn mtb-apbtn--reject"
                    onClick={() => setRejectOpen((v) => !v)}
                    disabled={apBusy}
                    aria-expanded={rejectOpen}
                    aria-label={tProject('project.mytask.ap_reject_aria', { name: task?.title ?? seed.title })}
                  >
                    <RotateCcw size={13} aria-hidden />
                    {tProject('project.mytask.ap_reject')}
                  </button>
                </>
              ) : (
                <span className="mtp-apnote">
                  <Clock size={13} aria-hidden />
                  {tProject('project.mytask.ap_pending')}
                </span>
              )
            ) : (
              <button
                type="button"
                className="mtp-done"
                onClick={() => void runApproval(() => submitTaskForApproval(taskId))}
                disabled={apBusy || blocked}
                data-tip={blocked
                  ? tProject('project.mytask.confirm_left', { n: String(remaining) })
                  : tProject('project.mytask.ap_submit_hint')}
              >
                {tProject('project.mytask.ap_submit')}
              </button>
            )
          ) : (
          <button
            type="button"
            className={`mtp-done${status === 'DONE' ? ' mtp-done--undo' : ''}`}
            onClick={() => void toggleDone()}
            disabled={busy || blocked}
            data-tip={blocked ? tProject('project.mytask.confirm_left', { n: String(remaining) }) : undefined}
          >
            {tProject(status === 'DONE' ? 'project.mytask.undo_done' : 'project.mytask.mark_done')}
          </button>
          )}
          {/* 닫기 — 머리 오른쪽에 있던 것을 완료 옆으로 내렸다. */}
          <button type="button" className="mtp-foot-close" onClick={onClose} disabled={busy}>
            <X size={13} aria-hidden />
            {tProject('project.btn.close')}
          </button>
          <button type="button" className="mtp-goto" onClick={onGoToProject}>
            {tProject('project.mytask.open_project')}
            <ArrowRight size={13} aria-hidden />
          </button>
          </div>
          {/*
            반려 사유 — [반려] 를 누른 뒤에만 편다. 사유는 선택이다(비워도 반려된다).
            확인창(layerConfirm)이 아니라 인라인인 이유: 사유를 적는 곳이 모달이면 목록과
            업무 내용이 가려져, 무엇을 왜 돌려보내는지 보면서 쓸 수가 없다.
          */}
          {rejectOpen && (
            <div className="mtp-apreject">
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={tProject('project.mytask.ap_reject_ph')}
                maxLength={300}
                disabled={apBusy}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); void runApproval(() => rejectTask(taskId, rejectReason)); }
                  if (e.key === 'Escape') { e.preventDefault(); setRejectOpen(false); }
                }}
              />
              <button
                type="button"
                className="mtb-apbtn mtb-apbtn--reject"
                onClick={() => void runApproval(() => rejectTask(taskId, rejectReason))}
                disabled={apBusy}
              >
                {tProject('project.mytask.ap_reject_confirm')}
              </button>
            </div>
          )}
          {apError && <div className="pj-error" role="alert">{apError}</div>}
        </footer>
      </aside>
    </>
  );
}
